import React, { useEffect, useMemo, useState } from 'react';
import { DeveloperAppReview } from '../../../models/app';
import { Paper } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { typeMatch } from '../../../utils/helpers';
import { ReviewAppForm } from '../AppActions/ReviewApp/review-app-form';
import { ReviewsList } from './reviews-list';
import {
  AppDetailsModuleId,
  AppDetailsState,
} from '../../../pages/AppDetails/types';
import {
  useFetchAppReviewsQuery,
} from '../../../redux/api/appMarket-api-slice';
import { EntityState } from '@reduxjs/toolkit';
import { canCreateNewReview } from '../../../component-helpers/app-reviews';
import { DeleteReviewResponse } from '../../../models/post-req-responses-payloads';
import { AppActionPostRequestResponseHandler, SubmitReviewRequestHandler } from '../AppActions/types';
import { usePermission } from '../../../hooks/usePermission';
import { AppMarketPermissions } from '../../../models/enums';
import BigAggregateRatingWithStars from '../../BigAggregateRatingWithStars';
import commonStyles from '../../../styles/commonStyles.module.scss';

interface ComponentProps {
  moduleId: AppDetailsModuleId;
  state: AppDetailsState;
  handlePostReq: AppActionPostRequestResponseHandler;
}

export interface ReviewsConfig {
  limit: number;
  showAll: boolean;
}

const MAX_REVIEW_LIST_LIMIT = 60;
const MIN_REVIEW_LIST_LIMIT = 2;

const getReviewCount = (totalReviewCount: Maybe<number>): number => {
  if (typeMatch(totalReviewCount, 'number')) {
      return totalReviewCount!;
  } else return 0;
};

export const AppRatingsAndReviews: React.FC<ComponentProps> = (
  {
    moduleId,
    state,
    handlePostReq,
  }): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const { canShowAggregateRating, app } = state;
  const { aggregateRating, id: appId, name: appName } = app!;
  const { enabled: hasInstallUninstallPermission } = usePermission(AppMarketPermissions.InstallUninstallApps);
  const [totalReviewCount, setReviewCount] = useState<number>(getReviewCount(aggregateRating.reviewCount));
  const [reviewsListConfig, setReviewsListConfig] = useState<ReviewsConfig>({
    limit: MIN_REVIEW_LIST_LIMIT,
    showAll: false,
  });
  const [reviews, setReviews] = useState<EntityState<DeveloperAppReview>>();

  const { data: appReviews, isFetching: appListIsFetching } = useFetchAppReviewsQuery({
    appId,
    limit: reviewsListConfig.limit,
    offset: 0,
  });
  const permissionToCreateReview = useMemo(() => canCreateNewReview(app, reviews, hasInstallUninstallPermission), [app, hasInstallUninstallPermission, reviews]);
  const isThereAnythingToShow = reviews?.ids.length || canShowAggregateRating || permissionToCreateReview;

  useEffect(() => {
    if (appReviews) setReviews(appReviews); // App reviews are added to the state of this component, so that the reviews list can be optimistically changed, before a new list of reviews arrive.
  }, [appReviews]);

  useEffect(() => {
    setReviewCount(getReviewCount(aggregateRating.reviewCount));
  }, [aggregateRating.reviewCount]);

  const readMoreReviews = (): void => {
    setReviewsListConfig({
      limit: MAX_REVIEW_LIST_LIMIT,
      showAll: true,
    });
  };

  const readLessReviews = (): void => {
    setReviewsListConfig({
      limit: reviewsListConfig.limit,
      showAll: false,
    });
  };

  const reviewRemovedHandler: AppActionPostRequestResponseHandler<DeleteReviewResponse> = (args): void => {
    const { reqResponse } = args;
    if (reqResponse && 'data' in reqResponse && reqResponse.data) {
      let newReviews: EntityState<DeveloperAppReview> = JSON.parse(JSON.stringify(reviews));
      newReviews.ids.shift(); // only the first review (which is an isSelf review) can be deleted
      setReviewCount(totalReviewCount - 1);
      setReviews(newReviews);
    }
    handlePostReq(args);
  };

  const reviewSubmittedCallBack: SubmitReviewRequestHandler = (args) => {
    const { reqResponse, reviewId } = args;
    if (reqResponse && 'data' in reqResponse) {
      let { data } = reqResponse;
      let newReviews: EntityState<DeveloperAppReview>;
      if (!reviews) { // this is the case where initially review count was zero. Thus this would have skipped the fetchAppReviews call
        newReviews = {
          ids: [],
          entities: {},
        };
      } else {
        newReviews = JSON.parse(JSON.stringify(reviews));
      }
      if (!reviewId) {
        //new review
        const { ids, entities } = newReviews;
        const { id: submittedReviewId } = data;
        ids.unshift(submittedReviewId);
        entities[submittedReviewId] = data;
        setReviewCount(totalReviewCount + 1);
      } else {
        // updated review
        newReviews.entities[reviewId] = data;
      }
      setReviews(newReviews);
    }
    handlePostReq(args);
  };

  if (!isThereAnythingToShow) return null;

  return (
    <Paper square id={moduleId}>
      <div className={styles.reviewsHeader}>
        <h3>{t('app_details.ratings_and_reviews')}</h3>
        {
          canShowAggregateRating &&
          <BigAggregateRatingWithStars aggregateRating={aggregateRating} reviewCount={totalReviewCount} />
        }
        {
          permissionToCreateReview &&
          <>
            <hr className={commonStyles.hr} />
            <ReviewAppForm appId={appId} appName={appName} reviewSubmittedCallBack={reviewSubmittedCallBack} />
          </>
        }
      </div>
      {
        reviews &&
        <ReviewsList
          isFetching={appListIsFetching}
          minReviewListLimit={MIN_REVIEW_LIST_LIMIT}
          reviews={reviews}
          readMore={readMoreReviews}
          readLess={readLessReviews}
          reviewsListConfig={reviewsListConfig}
          totalReviewCount={totalReviewCount}
          reviewRemovedHandler={reviewRemovedHandler}
          reportReviewHandler={handlePostReq}
          reviewSubmittedCallBack={reviewSubmittedCallBack}
        />
      }
    </Paper>
  );
};
